import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { db, storage } from "../../../firebase";
import {
  collection,
  getDocs,
  addDoc,
  collectionGroup,
  where,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { notifyTopRight, notifyError } from "../toast/SetToast";
import Toast from "../toast/SetToast";
import { Button, Modal, Form, Dropdown } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import MaskInventoryTable from "./MaskInventoryTable";
import { MaskSizes } from "./formdata/MaskInventoryData";

const MaskInventory = () => {
  const { currentUser } = useAuth();

  const [textSearch, setTextSearch] = useState("");
  const [sortBy, setSortBy] = useState("Mask Name");
  const [sizeFilter, setSizeFilter] = useState("ทั้งหมด");
  const [showModal, setShowModal] = useState(false);
  const [maskLogs, setMaskLogs] = useState([]);
  const [customMaskName, setCustomMaskName] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [inventory, setInventory] = useState([]);
  const [maskOptions, setMaskOptions] = useState([]);
  const [nodeOptions, setNodeOptions] = useState([]);
  const [centerFilter, setCenterFilter] = useState("BD");
  const allowedCenters = ["BD", "LPH"];

  const [newItem, setNewItem] = useState({
    itemId: "",
    maskName: "",
    size: "",
    price: "",
    imageUrl: "",
    available: "",
    borrowed: 0,
    rented: 0,
    createdBy: "",
    createdAt: "",
  });

  const onSearchChange = (e) => {
    setTimeout(() => {
      setTextSearch(e.target.value);
    }, 800);
  };

  const onFilterChange = (e) => {
    setTimeout(() => {
      setSortBy(e.target.value);
    }, 800);
  };

  const onSizeChange = (selectedKey) => {
    setSizeFilter(selectedKey);
  };

  const fetchInventory = async () => {
    try {
      const inventoryRef = collection(db, "maskInventory");
      let queryConstraints = [];

      if (centerFilter) {
        queryConstraints.push(where("center", "==", centerFilter));
      }

      if (textSearch) {
        queryConstraints.push(where("maskName", ">=", textSearch));
        queryConstraints.push(where("maskName", "<=", textSearch + "\uf8ff"));
      }
      if (sizeFilter !== "ทั้งหมด") {
        queryConstraints.push(where("size", "==", sizeFilter));
      }

      const inventoryQuery = query(inventoryRef, ...queryConstraints);
      const snapshot = await getDocs(inventoryQuery);

      const inventoryData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log("Mask Inventory Data:", inventoryData);
      return inventoryData;
    } catch (error) {
      console.error("Error fetching mask inventory:", error);
      return [];
    }
  };

  const fetchLogs = async () => {
    try {
      const collectionRef = collectionGroup(db, "maskLogs");
      let queryConstraints = [];

      if (centerFilter) {
        queryConstraints.push(where("center", "==", centerFilter));
      }

      if (textSearch) {
        queryConstraints.push(where("maskName", ">=", textSearch));
        queryConstraints.push(where("maskName", "<=", textSearch + "\uf8ff"));
      }
      if (sizeFilter !== "ทั้งหมด") {
        queryConstraints.push(where("size", "==", sizeFilter));
      }

      const logQuery = query(collectionRef, ...queryConstraints);
      const snapshot = await getDocs(logQuery);

      const historyData = snapshot.docs.map((doc) => {
        const data = doc.data();
        const formattedDatetime = data.datetime && data.datetime.seconds
          ? new Date(
              (data.datetime.seconds + 7 * 60 * 60) * 1000
            ).toLocaleString()
          : "Unknown Date";

        return {
          id: doc.id,
          ...data,
          datetime: formattedDatetime,
        };
      });

      console.log("Mask History Data:", historyData);
      return historyData;
    } catch (error) {
      console.error("Error fetching mask history:", error);
      return [];
    }
  };

  const fetchMaskOptions = async () => {
    try {
      const masksRef = collection(db, "masks");
      const masksQuery = query(
        masksRef,
        where("IsActive", "==", "Active"),
        orderBy("maskName", "asc")
      );
      const snapshot = await getDocs(masksQuery);
      const maskData = snapshot.docs.map((doc) => ({
        maskName: doc.data().maskName,
        price: doc.data().price,
      }));
      setMaskOptions(maskData);
    } catch (error) {
      console.error("Error fetching mask options:", error);
    }
  };

  const getData = async () => {
    const data = await fetchInventory();
    const log = await fetchLogs();
    setInventory(data);
    setMaskLogs(log);
  };

  const fetchNodes = async () => {
    try {
      const nodesRef = collection(db, "nodes");
      const snapshot = await getDocs(nodesRef);
      const codes = snapshot.docs.map((doc) => doc.data().nodecode).filter(Boolean);
      const filtered = codes.filter((c) => allowedCenters.includes(c));
      setNodeOptions(filtered);
      if (filtered.length > 0 && !filtered.includes(centerFilter)) {
        setCenterFilter(filtered[0]);
      }
    } catch (error) {
      console.error("Error fetching nodes:", error);
    }
  };

  useEffect(() => {
    fetchMaskOptions();
    fetchNodes();
  }, []);

  useEffect(() => {
    getData();
  }, [textSearch, sizeFilter, centerFilter]);

  const handleInputChange = (e) => {
    setNewItem({ ...newItem, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !newItem.itemId ||
      !newItem.maskName ||
      !newItem.size ||
      !newItem.price ||
      !newItem.available
    ) {
      notifyError("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const itemData = {
        ...newItem,
        price: parseFloat(newItem.price),
        available: parseInt(newItem.available),
        borrowed: 0,
        rented: 0,
        createdBy: currentUser.displayName,
        createdAt: Timestamp.now(),
        center: centerFilter,
      };

      const inventoryRef = collection(db, "maskInventory");
      const docRef = await addDoc(inventoryRef, itemData);

      // Upload image if selected
      if (selectedImage) {
        const storageRef = ref(
          storage,
          `maskInventory/${docRef.id}/${selectedImage.name}`
        );
        await uploadBytes(storageRef, selectedImage);
        const downloadURL = await getDownloadURL(storageRef);

        // Update the document with the image URL
        const { updateDoc, doc } = await import("firebase/firestore");
        await updateDoc(doc(db, "maskInventory", docRef.id), {
          imageUrl: downloadURL,
        });
      }

      notifyTopRight("Item added successfully");
      setShowModal(false);
      setNewItem({
        itemId: "",
        maskName: "",
        size: "",
        price: "",
        imageUrl: "",
        available: "",
        borrowed: 0,
        rented: 0,
        createdBy: "",
        createdAt: "",
      });
      setSelectedImage(null);
      setImagePreview(null);
      setCustomMaskName(false);

      setTimeout(() => {
        getData();
      }, 500);
    } catch (error) {
      console.error("Error adding mask inventory:", error);
      notifyError("Error adding item");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setNewItem({
      itemId: "",
      maskName: "",
      size: "",
      price: "",
      imageUrl: "",
      available: "",
      borrowed: 0,
      rented: 0,
      createdBy: "",
      createdAt: "",
    });
    setSelectedImage(null);
    setImagePreview(null);
    setCustomMaskName(false);
  };

  return (
    <React.Fragment>
      <Toast />
      <PageTitle activeMenu="Mask-Inventory" motherMenu="CPAP" />
      <div className="form-head d-flex mb-3 mb-lg-5 align-items-start">
        <Button variant="danger" onClick={() => setShowModal(true)}>
          + New Item
        </Button>
        <div className="input-group search-area ml-auto d-inline-flex">
          <input
            type="text"
            className="form-control"
            placeholder="Search by mask name"
            onChange={onSearchChange}
          />
          <div className="input-group-append">
            <span className="input-group-text c-pointer">
              <i className="flaticon-381-search-2"></i>
            </span>
          </div>
        </div>
        <Dropdown className="ml-3 width200" onSelect={onFilterChange}>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
            <i className="flaticon-381-controls-3 "></i> {sortBy}
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu-right">
            <Dropdown.Item href="#" eventKey={"Mask Name"}>
              Mask Name
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="ml-1 width200" onSelect={(key) => setCenterFilter(key)}>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-center">
            <i className="flaticon-381-location"></i> Center: {centerFilter}
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu-right">
            {nodeOptions.map((node) => (
              <Dropdown.Item key={node} href="#" eventKey={node}>
                {node}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>

        <Dropdown className="ml-1 width200" onSelect={onSizeChange}>
          <Dropdown.Toggle variant="outline-primary" id="dropdown-basic">
            <i className="flaticon-381-menu-1"></i> Size: {sizeFilter}
          </Dropdown.Toggle>
          <Dropdown.Menu className="dropdown-menu-right">
            <Dropdown.Item href="#" eventKey={"ทั้งหมด"}>
              ทั้งหมด
            </Dropdown.Item>
            {MaskSizes.map((size) => (
              <Dropdown.Item key={size} href="#" eventKey={size}>
                {size}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <MaskInventoryTable
        inventoryList={inventory}
        fetchInventory={getData}
        maskLogs={maskLogs}
        maskOptions={maskOptions}
        centerFilter={centerFilter}
        nodeOptions={nodeOptions}
      />

      {/* Add Item Modal */}
      <Modal show={showModal} onHide={resetModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add New Mask Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>
                Center <span className="text-danger">*</span>
              </Form.Label>
              <select
                name="center"
                className="form-control"
                value={centerFilter}
                onChange={(e) => setCenterFilter(e.target.value)}
                required
              >
                <option value="">Select center</option>
                {nodeOptions.map((node) => (
                  <option key={node} value={node}>
                    {node}
                  </option>
                ))}
              </select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Item ID <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="itemId"
                value={newItem.itemId}
                onChange={handleInputChange}
                placeholder="e.g., M001"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Mask Name <span className="text-danger">*</span>
              </Form.Label>
              <select
                name="maskName"
                className="form-control"
                value={customMaskName ? "custom" : newItem.maskName}
                onChange={(e) => {
                  if (e.target.value === "custom") {
                    setCustomMaskName(true);
                    setNewItem({ ...newItem, maskName: "", price: "" });
                  } else {
                    setCustomMaskName(false);
                    const selectedMask = maskOptions.find(
                      (m) => m.maskName === e.target.value
                    );
                    setNewItem({
                      ...newItem,
                      maskName: e.target.value,
                      price: selectedMask && selectedMask.price ? selectedMask.price : "",
                    });
                  }
                }}
              >
                <option value="">Select a mask</option>
                {maskOptions.map((mask) => (
                  <option key={mask.maskName} value={mask.maskName}>
                    {mask.maskName}
                  </option>
                ))}
                <option value="custom">Other (Specify)</option>
              </select>

              {customMaskName && (
                <Form.Control
                  type="text"
                  name="maskName"
                  placeholder="Enter custom mask name"
                  value={newItem.maskName}
                  onChange={handleInputChange}
                  className="mt-2"
                  required
                />
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Size <span className="text-danger">*</span>
              </Form.Label>
              <select
                name="size"
                className="form-control"
                value={newItem.size}
                onChange={handleInputChange}
                required
              >
                <option value="">Select size</option>
                {MaskSizes.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Price (THB) <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                name="price"
                value={newItem.price}
                onChange={handleInputChange}
                placeholder="e.g., 2500"
                min="0"
                step="0.01"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleImageChange}
              />
              {imagePreview && (
                <div className="mt-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "200px",
                      maxHeight: "200px",
                      objectFit: "cover",
                    }}
                  />
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                Available Quantity <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="number"
                name="available"
                value={newItem.available}
                onChange={handleInputChange}
                placeholder="e.g., 10"
                min="0"
                required
              />
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="mt-3"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Item"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </React.Fragment>
  );
};

export default MaskInventory;
